import React, { useCallback, useEffect, useState } from "react";
import { AlertOctagon, CheckCircle2, KeyRound, Loader2, RefreshCw, LogIn } from "lucide-react";
import { toast } from "sonner";
import client from "../../lib/api";

/** Modal for admin re-login flow — captures Moloni username/password and
 *  fires POST /admin/moloni/relogin. The password is submitted directly,
 *  never stored locally. On success, closes and refreshes the parent panel.
 */
const ReloginModal = ({ onClose, onDone }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!username.trim() || !password) {
      toast.error("Preenche o utilizador e a password Moloni.");
      return;
    }
    setBusy(true);
    try {
      const r = await client.post("/admin/moloni/relogin", {
        username: username.trim(), password,
      });
      toast.success(`Tokens renovados (válidos ${Math.round((r.data?.expires_in || 3600) / 60)}min).`);
      onDone?.();
      onClose?.();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "Falha a renovar tokens.";
      if (status === 429) {
        toast.error(detail, { duration: 10000 });
      } else {
        toast.error(detail);
      }
      // Clear the password on failure so the admin can't accidentally
      // spam-submit the same wrong value — each failed attempt counts
      // towards the Moloni lockout. Username kept for convenience.
      setPassword("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
         data-testid="moloni-relogin-modal">
      {/* Backdrop is inert on click — a password mid-type would otherwise be
          lost silently. Close via the explicit Cancelar button. */}
      <form onSubmit={submit}
            className="w-full max-w-md bg-[#161920] border border-white/10 rounded-2xl p-6"
            data-testid="moloni-relogin-form">
        <h3 className="font-display text-xl text-[#F5F2EE] mb-1">
          Renovar tokens Moloni
        </h3>
        <p className="text-sm text-[#A29F9B] mb-4">
          Introduz as credenciais actuais da tua conta Moloni. Se estiverem OK,
          gravamos os novos tokens e o circuit-breaker é libertado.
          <br />
          <strong className="text-amber-300">
            Cada tentativa falhada conta para o lockout do Moloni — só tenta se tiveres a certeza.
          </strong>
        </p>

        <label className="text-xs text-[#A29F9B] uppercase tracking-widest block mb-1">
          Utilizador Moloni
        </label>
        <input value={username} onChange={(e) => setUsername(e.target.value)}
               placeholder="Ex.: solfut@solfut.com"
               autoComplete="username"
               className="w-full bg-[#0C0E12] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F5F2EE] mb-3"
               data-testid="moloni-relogin-username" autoFocus />

        <label className="text-xs text-[#A29F9B] uppercase tracking-widest block mb-1">
          Password
        </label>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
               placeholder="••••••••••"
               autoComplete="current-password"
               className="w-full bg-[#0C0E12] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F5F2EE] mb-4"
               data-testid="moloni-relogin-password" />

        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose}
                  className="px-4 py-2 text-sm text-[#A29F9B] hover:text-white"
                  data-testid="moloni-relogin-cancel">
            Cancelar
          </button>
          <button type="submit" disabled={busy}
                  className="inline-flex items-center gap-2 bg-[#C5A880] text-[#0C0E12] font-semibold px-4 py-2 rounded-lg hover:bg-[#B89670] disabled:opacity-50"
                  data-testid="moloni-relogin-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
            Renovar
          </button>
        </div>
      </form>
    </div>
  );
};

/** Moloni auth health — surfaces the circuit-breaker state so the admin
 *  understands why invoices aren't being issued. Silent when everything is
 *  fine (auth working + no active lock). */
const MoloniStatusSection = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [reloginOpen, setReloginOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await client.get("/admin/moloni/status");
      setStatus(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao carregar status Moloni.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const unlock = async () => {
    if (!window.confirm(
      "Só usa isto depois de RENOVARES o refresh token do Moloni em produção. " +
      "Caso contrário volta a bloquear na próxima emissão e piora o lockout.",
    )) return;
    setUnlocking(true);
    try {
      await client.post("/admin/moloni/unlock");
      toast.success("Circuit-breaker libertado. Próxima emissão vai tentar autenticar.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao libertar lock.");
    } finally {
      setUnlocking(false);
    }
  };

  // Happy path — nothing to show
  if (!status || (!status.locked && status.configured && status.has_refresh_token)) {
    return null;
  }

  const fmtRemaining = (s) => {
    if (!s) return "—";
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };

  return (
    <section className={`rounded-2xl p-6 md:p-8 ${
              status.locked
                ? "bg-[#161920] border border-red-500/50"
                : "bg-[#161920] border border-amber-500/40"
            }`}
             data-testid="moloni-status-section">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <span className={`label-uppercase inline-flex items-center gap-2 ${
                  status.locked ? "text-red-300" : "text-amber-300"
                }`}>
            <AlertOctagon className="w-3.5 h-3.5" /> Moloni Auth
          </span>
          <h2 className="font-display text-2xl md:text-3xl mt-2 text-[#F5F2EE]">
            {status.locked
              ? "Emissão de faturas BLOQUEADA"
              : "Configuração incompleta"}
          </h2>
        </div>
        <button onClick={load} disabled={loading}
                className="text-xs text-[#A29F9B] hover:text-[#C5A880] inline-flex items-center gap-1.5"
                data-testid="moloni-status-refresh">
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refrescar
        </button>
      </div>

      {status.locked ? (
        <>
          <p className="text-sm text-[#DCD7D2] mb-2">
            O circuit-breaker está aberto — nenhuma fatura vai ser emitida durante
            este período. Isto protege a conta Moloni de ficar mais tempo bloqueada.
          </p>
          <div className="grid md:grid-cols-2 gap-3 text-sm mb-3">
            <div className="bg-[#0C0E12] border border-white/5 rounded-lg p-3">
              <div className="text-xs text-[#A29F9B] uppercase tracking-widest mb-1">Motivo</div>
              <div className="text-[#F5F2EE]">{status.lock_reason || "—"}</div>
            </div>
            <div className="bg-[#0C0E12] border border-white/5 rounded-lg p-3">
              <div className="text-xs text-[#A29F9B] uppercase tracking-widest mb-1">Termina em</div>
              <div className="text-[#F5F2EE] font-mono">{fmtRemaining(status.seconds_left)}</div>
              <div className="text-xs text-[#A29F9B] mt-1">{status.locked_until}</div>
            </div>
          </div>
          <div className="bg-amber-900/20 border border-amber-900/40 rounded-lg p-3 mb-3 text-sm text-amber-200">
            <b>Passo a passo:</b>
            <ol className="list-decimal pl-5 mt-1 space-y-1">
              <li>Vai a produção e renova o refresh token do Moloni (login manual + guardar novo token em <code>db.moloni_tokens</code> ou substituir credenciais em .env)</li>
              <li>Ou espera pelo fim do cooldown ({fmtRemaining(status.seconds_left)}) para tentar auto-recuperar</li>
              <li>Clica em &ldquo;Libertar circuit-breaker&rdquo; só DEPOIS de resolveres a autenticação</li>
            </ol>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => setReloginOpen(true)}
                    className="inline-flex items-center gap-2 bg-[#C5A880] text-[#0C0E12] font-semibold px-4 py-2 rounded-lg hover:bg-[#B89670]"
                    data-testid="moloni-status-relogin-open">
              <LogIn className="w-4 h-4" />
              Renovar tokens
            </button>
            <button onClick={unlock} disabled={unlocking}
                    className="inline-flex items-center gap-2 bg-red-600 border border-red-700 text-white font-semibold px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50"
                    data-testid="moloni-status-unlock">
              {unlocking ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
              Libertar circuit-breaker
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-[#DCD7D2] mb-3">
            Credenciais Moloni em falta.
            {!status.configured && " Variáveis MOLONI_CLIENT_ID/SECRET/USERNAME/PASSWORD não estão definidas."}
            {status.configured && !status.has_refresh_token && (
              <> Nunca foi feito login. Clica em &ldquo;Renovar tokens&rdquo; para autenticar agora.</>
            )}
          </p>
          {status.configured && (
            <button onClick={() => setReloginOpen(true)}
                    className="inline-flex items-center gap-2 bg-[#C5A880] text-[#0C0E12] font-semibold px-4 py-2 rounded-lg hover:bg-[#B89670]"
                    data-testid="moloni-status-relogin-open">
              <LogIn className="w-4 h-4" />
              Renovar tokens
            </button>
          )}
        </>
      )}
      {reloginOpen && (
        <ReloginModal onClose={() => setReloginOpen(false)}
                      onDone={load} />
      )}
    </section>
  );
};

export default MoloniStatusSection;
